import { useMemo, useState } from 'react'
import Seo, { softwareAppSchema } from '../components/Seo'
import Breadcrumbs from '../components/Breadcrumbs'
import Faq from '../components/Faq'
import type { QA } from '../components/Faq'
import ResultSheet from '../components/ResultSheet'
import CalcActions from '../components/CalcActions'
import InfoTip from '../components/InfoTip'
import { Field, SmartField, Segmented } from '../components/Inputs'
import { computeGravel, DEFAULT_GRAVEL, GRAVEL_TYPE_LABELS } from '../calculators/gravel'
import type { GravelInputs, GravelArea, GravelType } from '../calculators/gravel'
import { calcsInCategory } from '../data/calculators'
import { saveSheet, genId } from '../lib/storage'
import { useLocale, localizedHref } from '../i18n/locale'
import { t } from '../i18n/strings'
import { calcName, categoryName } from '../i18n/calculators'

const PATH = '/calculators/gravel/'
const CATEGORY = 'concrete-and-aggregate'

const GRAVEL_TYPE_LABELS_FR: Record<GravelType, string> = {
  'pea-gravel': 'Gravier de pois (105 lb/pi³)',
  'crushed-stone': 'Pierre concassée (100 lb/pi³)',
  'river-rock': 'Galets de rivière (90 lb/pi³)',
  'decomposed-granite': 'Granit décomposé (100 lb/pi³)',
}

const GRAVEL_OPTIONS: GravelType[] = ['pea-gravel', 'crushed-stone', 'river-rock', 'decomposed-granite']

interface CalcContent {
  title: string
  desc: string
  h1: string
  intro: string
  savePrefix: string
  area: string
  depth: string
  depthTip: string
  gravelType: string
  gravelTypeTip: string
  addFabric: string
  addFabricTip: string
  addEdging: string
  addEdgingTip: string
  addCompactor: string
  addCompactorTip: string
  wasteTip: string
  primaryName: string
  primaryLabel: string
  secondaryName: string
  secondaryLabel: string
  aboutH2: string
  aboutP1: string
  howH2: string
  howP1: string
  formula: string
  howP2: string
  mistakesH3: string
  relatedH2: string
  reviewer: string
  faqs: QA[]
}

const EN: CalcContent = {
  title: 'Gravel Calculator — Cubic Yards, Tons and Bags | RenoSheets',
  desc: 'Free gravel calculator. Enter area and depth to get cubic yards, tons and bag count for pea gravel, crushed stone, river rock or decomposed granite. No signup.',
  h1: 'Gravel Calculator',
  intro: 'Enter area and depth for cubic yards, tons and 50 lb bag count — for any gravel or aggregate type.',
  savePrefix: 'Gravel',
  area: 'Area',
  depth: 'Depth',
  depthTip: '2–3 in for decorative beds and paths. 4 in for driveways and functional base. 6 in for heavy vehicle traffic.',
  gravelType: 'Gravel type',
  gravelTypeTip: 'Type sets the density used to calculate weight. Crushed stone and DG are denser than river rock.',
  addFabric: 'Add landscape fabric',
  addFabricTip: 'Recommended under all gravel installs to prevent weed growth and keep the gravel clean.',
  addEdging: 'Add landscape edging',
  addEdgingTip: 'Edging keeps gravel from migrating into lawn or adjacent areas.',
  addCompactor: 'Add plate compactor rental',
  addCompactorTip: 'Compacting crushed stone and decomposed granite is essential for stable driveways and paths.',
  wasteTip: 'Extra for settling, uneven ground and edges. 10% is standard for gravel.',
  primaryName: 'Cubic yards',
  primaryLabel: 'of gravel (bulk)',
  secondaryName: '50 lb bags',
  secondaryLabel: 'for small jobs',
  aboutH2: 'What actually affects how much gravel you need',
  aboutP1: 'Depth matters more than most people expect, and it depends entirely on what the gravel is for. A decorative top layer over landscape fabric only needs 2–3 inches — any deeper and it just sinks into the ground beneath. A driveway or path base is a different job entirely: it needs 4–6 inches of compacted sub-base gravel to actually bear weight and drain properly, and skipping compaction is the single most common reason gravel driveways develop ruts and low spots within a year. Gravel type also affects how much settles over time — angular crushed stone locks together and compacts well, while smooth river rock stays loose and needs more frequent topping up. Edging isn\'t just cosmetic: without it, gravel migrates into the lawn every time it rains or gets driven over, so budget for edging on any area that borders grass or a walkway.',
  howH2: 'How to calculate gravel quantity',
  howP1: 'Gravel is a simple volume: area times depth. Convert to cubic yards for bulk orders or pounds for bags:',
  formula: `cubic feet = area (sq ft) × depth (in) ÷ 12
cubic yards = cubic feet ÷ 27
weight (lbs) = cubic feet × density (lbs/cu ft)
tons = weight ÷ 2000`,
  howP2: 'Density varies: pea gravel and crushed stone are 100–105 lbs/cu ft; river rock is 90 lbs/cu ft. Bulk delivery is cost-effective for any job over 0.5 cubic yards (about 25–30 bags).',
  mistakesH3: 'Common mistakes',
  relatedH2: 'Related calculators',
  reviewer: 'Reviewed by the RenoSheets team for calculation accuracy. Method: area × depth ÷ 27 for cubic yards; density from published aggregate values. Last updated 2026-05-24. Estimates only — verify quantities before purchasing.',
  faqs: [
    {
      q: 'How many cubic yards of gravel do I need for a 10x10 area?',
      a: 'A 10 × 10 ft area at 3 in deep needs 25 cubic feet, which is 0.93 cubic yards. Add 10% waste to get about 1.0 cubic yard. For a 4-inch depth, the same area needs about 1.2 cubic yards ordered.',
    },
    {
      q: 'How deep should gravel be for a driveway or path?',
      a: 'For a decorative path or patio, 2–3 inches is fine. For a functional driveway with vehicle traffic, use 4 inches of compacted crushed stone as a base. Heavy-use driveways benefit from 6 inches of sub-base plus 2 inches of top-dressing gravel.',
    },
    {
      q: 'How many tons of gravel do I need per cubic yard?',
      a: 'Crushed stone and pea gravel weigh roughly 1.35–1.4 tons per cubic yard. River rock is lighter at about 1.2 tons per yard. Use the "tons" figure when ordering from a quarry or landscape supplier who sells by weight.',
    },
    {
      q: 'How many 50 lb bags of gravel make a cubic yard?',
      a: 'A cubic yard of gravel weighs about 2,700 lbs. At 50 lbs per bag, that is 54 bags per cubic yard. For small jobs under about 0.5 cubic yards (27 bags), bagged gravel from a home center is convenient. For larger areas, bulk delivery is much cheaper.',
    },
    {
      q: 'What type of gravel is best for a driveway?',
      a: 'Crushed stone (also called road base or crusher run) is the best choice for driveways — the angular edges lock together when compacted. Pea gravel is round and shifts underfoot, making it poor for driveways but ideal for decorative beds. Decomposed granite packs firm and is popular for paths and patios in dry climates.',
    },
    {
      q: 'Do I need landscape fabric under gravel?',
      a: 'Yes, for decorative gravel in beds and paths, a layer of landscape fabric under the gravel dramatically reduces weed growth. Use heavy-duty woven fabric (not plastic sheeting, which prevents drainage). Overlap seams by at least 6 inches and pin every 2 square feet.',
    },
  ],
}

const FR: CalcContent = {
  title: 'Calculateur de gravier — Verges cubes, tonnes et sacs | RenoSheets',
  desc: 'Calculateur de gravier gratuit. Entrez la surface et la profondeur pour obtenir les verges cubes, les tonnes et le nombre de sacs pour le gravier de pois, la pierre concassée, les galets de rivière ou le granit décomposé. Sans inscription.',
  h1: 'Calculateur de gravier',
  intro: 'Entrez la surface et la profondeur pour les verges cubes, les tonnes et le nombre de sacs de 50 lb — pour tout type de gravier ou de granulat.',
  savePrefix: 'Gravier',
  area: 'Surface',
  depth: 'Profondeur',
  depthTip: '2–3 po pour les plates-bandes décoratives et les sentiers. 4 po pour les entrées de cour et les bases fonctionnelles. 6 po pour la circulation de véhicules lourds.',
  gravelType: 'Type de gravier',
  gravelTypeTip: 'Le type fixe la densité utilisée pour calculer le poids. La pierre concassée et le granit décomposé sont plus denses que les galets de rivière.',
  addFabric: 'Ajouter la membrane géotextile',
  addFabricTip: 'Recommandée sous toute pose de gravier pour empêcher la pousse des mauvaises herbes et garder le gravier propre.',
  addEdging: 'Ajouter la bordure de paysagement',
  addEdgingTip: 'La bordure empêche le gravier de migrer dans le gazon ou les zones adjacentes.',
  addCompactor: 'Ajouter la location d’une plaque vibrante',
  addCompactorTip: 'Le compactage de la pierre concassée et du granit décomposé est essentiel pour des entrées et des sentiers stables.',
  wasteTip: 'En plus pour le tassement, le sol inégal et les bordures. 10 % est la norme pour le gravier.',
  primaryName: 'Verges cubes',
  primaryLabel: 'de gravier (en vrac)',
  secondaryName: 'Sacs de 50 lb',
  secondaryLabel: 'pour les petits travaux',
  aboutH2: 'Ce qui influence vraiment la quantité de gravier nécessaire',
  aboutP1: 'La profondeur compte plus qu’on le pense, et elle dépend entièrement de l’usage du gravier. Une couche décorative sur une toile géotextile n’a besoin que de 2 à 3 pouces — plus profond, elle s’enfonce simplement dans le sol en dessous. Une base d’entrée ou de sentier est un tout autre travail : elle demande 4 à 6 pouces de gravier de sous-base compacté pour vraiment supporter le poids et bien drainer, et sauter le compactage est la raison la plus courante pour laquelle les entrées de gravier développent des ornières et des creux en moins d’un an. Le type de gravier influence aussi ce qui se tasse avec le temps — la pierre concassée angulaire s’imbrique et se compacte bien, tandis que le galet de rivière lisse reste meuble et demande des ajouts plus fréquents. La bordure n’est pas seulement esthétique : sans elle, le gravier migre vers la pelouse chaque fois qu’il pleut ou qu’on roule dessus, alors prévoyez une bordure pour toute zone qui touche du gazon ou une allée.',
  howH2: 'Comment calculer la quantité de gravier',
  howP1: 'Le gravier est un volume simple : surface multipliée par la profondeur. Convertissez en verges cubes pour les commandes en vrac ou en livres pour les sacs :',
  formula: `pieds cubes = surface (pi²) × profondeur (po) ÷ 12
verges cubes = pieds cubes ÷ 27
poids (lb) = pieds cubes × densité (lb/pi³)
tonnes = poids ÷ 2000`,
  howP2: 'La densité varie : le gravier de pois et la pierre concassée font 100–105 lb/pi³; les galets de rivière font 90 lb/pi³. La livraison en vrac est rentable pour tout projet de plus de 0,5 verge cube (environ 25–30 sacs).',
  mistakesH3: 'Erreurs fréquentes',
  relatedH2: 'Calculateurs connexes',
  reviewer: 'Révisé par l’équipe RenoSheets pour l’exactitude des calculs. Méthode : surface × profondeur ÷ 27 pour les verges cubes; densité tirée des valeurs publiées sur les granulats. Dernière mise à jour 2026-05-24. Estimations seulement — vérifiez les quantités avant d’acheter.',
  faqs: [
    {
      q: 'Combien de verges cubes de gravier me faut-il pour une surface de 10 x 10?',
      a: 'Une surface de 10 × 10 pi à 3 po de profondeur demande 25 pieds cubes, soit 0,93 verge cube. Ajoutez 10 % de perte pour obtenir environ 1,0 verge cube. À 4 pouces de profondeur, la même surface demande environ 1,2 verge cube à commander.',
    },
    {
      q: 'Quelle profondeur de gravier pour une entrée de cour ou un sentier?',
      a: 'Pour un sentier décoratif ou un patio, 2–3 pouces suffisent. Pour une entrée fonctionnelle avec circulation de véhicules, utilisez 4 pouces de pierre concassée compactée comme base. Les entrées très fréquentées profitent de 6 pouces de sous-base plus 2 pouces de gravier de finition.',
    },
    {
      q: 'Combien de tonnes de gravier par verge cube?',
      a: 'La pierre concassée et le gravier de pois pèsent environ 1,35–1,4 tonne par verge cube. Les galets de rivière sont plus légers, à environ 1,2 tonne par verge. Utilisez le chiffre en « tonnes » lorsque vous commandez d’une carrière ou d’un fournisseur en aménagement qui vend au poids.',
    },
    {
      q: 'Combien de sacs de 50 lb de gravier font une verge cube?',
      a: 'Une verge cube de gravier pèse environ 2 700 lb. À 50 lb par sac, cela fait 54 sacs par verge cube. Pour les petits travaux de moins de 0,5 verge cube (27 sacs), le gravier en sac d’un centre de rénovation est pratique. Pour les grandes surfaces, la livraison en vrac est bien plus économique.',
    },
    {
      q: 'Quel type de gravier convient le mieux à une entrée de cour?',
      a: 'La pierre concassée (aussi appelée base de route ou pierre brute) est le meilleur choix pour les entrées — les arêtes anguleuses s’imbriquent au compactage. Le gravier de pois est rond et se déplace sous les pieds, ce qui le rend mauvais pour les entrées mais idéal pour les plates-bandes décoratives. Le granit décomposé se compacte fermement et est populaire pour les sentiers et patios en climat sec.',
    },
    {
      q: 'Ai-je besoin de membrane géotextile sous le gravier?',
      a: 'Oui, pour le gravier décoratif dans les plates-bandes et les sentiers, une couche de membrane géotextile sous le gravier réduit considérablement la pousse des mauvaises herbes. Utilisez une membrane tissée robuste (pas une feuille de plastique, qui empêche le drainage). Chevauchez les joints d’au moins 6 pouces et fixez tous les 2 pieds carrés.',
    },
  ],
}

export default function GravelCalculator() {
  const locale = useLocale()
  const c = locale === 'fr' ? FR : EN
  const L = (en: string) => localizedHref(en, locale)
  const tr = (k: string) => t(locale, k)

  const [i, setI] = useState<GravelInputs>(DEFAULT_GRAVEL)
  const [saved, setSaved] = useState(false)
  const r = useMemo(() => computeGravel(i, locale), [i, locale])
  const set = (patch: Partial<GravelInputs>) => {
    setI((p) => ({ ...p, ...patch }))
    setSaved(false)
  }
  const setArea = (idx: number, patch: Partial<GravelArea>) =>
    set({ areas: i.areas.map((a, k) => (k === idx ? { ...a, ...patch } : a)) })
  const addArea = () => set({ areas: [...i.areas, { length: 10, width: 10 }] })
  const removeArea = (idx: number) => set({ areas: i.areas.filter((_, k) => k !== idx) })
  const metric = i.unit === 'metric'
  const lin = metric ? 'm' : (locale === 'fr' ? 'pi' : 'ft')
  const inUnit = locale === 'fr' ? 'po' : 'in'

  const typeLabel = (g: GravelType) => (locale === 'fr' ? GRAVEL_TYPE_LABELS_FR[g] : GRAVEL_TYPE_LABELS[g])

  // Localized unit labels.
  const cuFt = locale === 'fr' ? 'pi³' : 'cu ft'
  const sqft = locale === 'fr' ? 'pi²' : 'sq ft'
  const tons = locale === 'fr' ? 'tonnes' : 'tons'

  // Volume display tracks the unit system: cu ft when imperial (matches "Feet"),
  // m³ when metric. Bulk delivery is still quoted by the cu yd / m³, which
  // shows up in the side note below the waste slider.
  const volumeQty = metric ? r.cubicMeters : r.cubicFeetWithWaste
  const volumeUnit = metric ? 'm³' : cuFt

  const onSave = async () => {
    const areaUnit = metric ? 'm²' : sqft
    const areaVal = metric ? r.totalAreaM2 : r.totalArea
    const bagWord = locale === 'fr' ? 'sacs de 50 lb' : 'bags of 50 lb'
    await saveSheet({
      id: genId(),
      type: 'gravel',
      name: `${c.savePrefix} — ${areaVal} ${areaUnit}`,
      createdAt: new Date().toISOString(),
      summary: `${volumeQty} ${volumeUnit} · ${r.tons} ${tons} · ${r.bags50lb} ${bagWord}`,
      lines: r.lines.filter((l) => l.kind === 'consumable').map((l) => ({ label: l.label, qty: l.qty, unit: l.unit })),
    })
    setSaved(true)
  }

  const related = calcsInCategory(CATEGORY).filter((x) => x.slug !== 'gravel')

  const depthCm = (i.depthIn * 2.54).toFixed(1).replace(/\.0$/, '')
  const rawM3 = (r.cubicFeet * 0.028316846592).toFixed(2)
  const assumptions = (() => {
    if (metric) {
      return locale === 'fr'
        ? `${r.totalAreaM2} m² à ${depthCm} cm de profondeur · ${i.wastePct} % de perte · ${r.tons} tonnes.`
        : `${r.totalAreaM2} m² at ${depthCm} cm deep · ${i.wastePct}% waste · ${r.tons} tons.`
    }
    return locale === 'fr'
      ? `${r.totalArea} pi² à ${i.depthIn} po de profondeur · ${i.wastePct} % de perte · ${r.tons} tonnes.`
      : `${r.totalArea} sq ft at ${i.depthIn} in deep · ${i.wastePct}% waste · ${r.tons} tons.`
  })()

  // Step-by-step "How we got this" content for the ⓘ button on the result card.
  // Mirrors computeGravel: sum areas, × depth (in/12), × (1 + waste%), ÷ 27 to
  // cu yd, × density / 2000 to tons, ÷ 50 lb bag.
  const cuYdWord = locale === 'fr' ? 'verges cubes' : 'cu yd'
  const density = i.gravelType === 'pea-gravel' ? 105 : i.gravelType === 'river-rock' ? 90 : 100
  const buildExplain = (): React.ReactNode => (
    <ol className="list-decimal space-y-1 pl-5">
      <li>
        {i.inputMode === 'area'
          ? (locale === 'fr' ? 'Surface saisie' : 'Area you entered')
          : (locale === 'fr'
              ? `Surface totale (${i.areas.length} ${i.areas.length === 1 ? 'zone' : 'zones'})`
              : `Total area (${i.areas.length} ${i.areas.length === 1 ? 'area' : 'areas'})`)}:
        {' '}<strong>{r.totalArea} {sqft}</strong>
      </li>
      <li>
        {locale === 'fr' ? `× profondeur ${i.depthIn}/12 pi` : `× depth ${i.depthIn}/12 ft`}:
        {' '}<strong>{r.cubicFeet} {cuFt}</strong>
      </li>
      <li>{locale === 'fr' ? `+ ${i.wastePct} % de perte` : `+ ${i.wastePct}% waste`}: <strong>{r.cubicFeetWithWaste} {cuFt}</strong></li>
      <li>{locale === 'fr' ? '÷ 27' : '÷ 27'} = <strong>{r.cubicYards} {cuYdWord}</strong></li>
      <li>
        {locale === 'fr' ? `× ${density} lb/pi³ ÷ 2000` : `× ${density} lbs/cu ft ÷ 2000`}
        {' '}= <strong>{r.tons} {tons}</strong>
      </li>
      <li>
        {locale === 'fr' ? `÷ 50 lb par sac` : `÷ 50 lb per bag`} = <strong>{r.bags50lb} {locale === 'fr' ? (r.bags50lb === 1 ? 'sac' : 'sacs') : (r.bags50lb === 1 ? 'bag' : 'bags')}</strong>
      </li>
    </ol>
  )

  return (
    <div className="mx-auto max-w-6xl px-4 py-6">
      <Seo title={c.title} description={c.desc} path={PATH} schema={softwareAppSchema(calcName('gravel', 'Gravel Calculator', locale), c.desc, localizedHref(PATH, locale))} />
      <Breadcrumbs
        items={[
          { name: tr('crumb.home'), href: L('/') },
          { name: categoryName(CATEGORY, 'Concrete & Aggregate', locale), href: L(`/calculators/${CATEGORY}/`) },
          { name: calcName('gravel', 'Gravel Calculator', locale), href: L(PATH) },
        ]}
      />

      <h1 className="text-3xl">{c.h1}</h1>
      <p className="mt-1 max-w-content text-text-muted">{c.intro}</p>

      <div className="mt-6 grid gap-6 lg:grid-cols-5">
        <form className="rounded-xl border border-border bg-surface p-5 lg:col-span-3" onSubmit={(e) => e.preventDefault()}>
          <div className="mb-5 flex flex-wrap gap-6">
            <Segmented
              label={tr('calc.input')}
              value={i.inputMode}
              onChange={(v) => {
                if (v === 'area' && i.inputMode === 'dimensions') {
                  const sum = i.areas.reduce((s, a) => s + Math.max(0, a.length) * Math.max(0, a.width), 0)
                  set({ inputMode: v, totalArea: Math.round(sum * 10) / 10 })
                } else {
                  set({ inputMode: v })
                }
              }}
              options={[{ v: 'dimensions', l: tr('calc.dimensions') }, { v: 'area', l: tr('calc.totalArea') }]}
            />
          </div>

          {i.inputMode === 'dimensions' ? (
            <div className="space-y-3">
              {i.areas.map((a, idx) => (
                <div key={idx} className="rounded-md border border-border p-3">
                  <div className="mb-2 flex items-center justify-between">
                    <span className="text-sm font-medium text-text-body">{c.area} {idx + 1}</span>
                    {i.areas.length > 1 && (
                      <button type="button" onClick={() => removeArea(idx)} className="text-xs text-danger hover:underline">{tr('calc.remove')}</button>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <SmartField label={tr('calc.length')} imperialValue={a.length} onChangeImperial={(n) => setArea(idx, { length: n })} imperialUnit="ft" />
                    <SmartField label={tr('calc.width')} imperialValue={a.width} onChangeImperial={(n) => setArea(idx, { width: n })} imperialUnit="ft" />
                  </div>
                </div>
              ))}
              <button type="button" onClick={addArea} className="w-full rounded-md border border-dashed border-border py-2 text-sm font-medium text-brand-text hover:bg-surface-2">
                {tr('calc.addArea')}
              </button>
            </div>
          ) : (
            <div className="rounded-md border border-border p-3">
              <SmartField
                label={tr('calc.totalArea')}
                imperialValue={i.totalArea}
                onChangeImperial={(n) => set({ totalArea: n })}
                imperialUnit="sq ft"
              />
              <p className="mt-2 text-xs text-text-subtle">{tr('calc.areaHint')}</p>
            </div>
          )}

          {/* Depth */}
          <div className="mt-4">
            <span className="mb-1 flex items-center text-sm font-medium text-text-body">
              {c.depth}
              <InfoTip text={c.depthTip} />
            </span>
            <SmartField label="" imperialValue={i.depthIn} onChangeImperial={(n) => set({ depthIn: n })} imperialUnit="in" />
          </div>

          {/* Gravel type */}
          <div className="mt-4">
            <span className="mb-1 flex items-center text-sm font-medium text-text-body">
              {c.gravelType}
              <InfoTip text={c.gravelTypeTip} />
            </span>
            <select
              value={i.gravelType}
              onChange={(e) => set({ gravelType: e.target.value as GravelType })}
              className="w-full rounded-md border border-border bg-surface px-3 py-2.5 text-sm text-text focus:border-brand focus:outline-none"
            >
              {GRAVEL_OPTIONS.map((g) => (
                <option key={g} value={g}>{typeLabel(g)}</option>
              ))}
            </select>
          </div>

          {/* Options */}
          <div className="mt-4 space-y-2">
            <label className="flex items-center gap-2 text-sm text-text-body">
              <input type="checkbox" checked={i.needFabric} onChange={(e) => set({ needFabric: e.target.checked })} />
              {c.addFabric}
              <InfoTip text={c.addFabricTip} />
            </label>
            <label className="flex items-center gap-2 text-sm text-text-body">
              <input type="checkbox" checked={i.needEdging} onChange={(e) => set({ needEdging: e.target.checked })} />
              {c.addEdging}
              <InfoTip text={c.addEdgingTip} />
            </label>
            {(i.gravelType === 'crushed-stone' || i.gravelType === 'decomposed-granite') && (
              <label className="flex items-center gap-2 text-sm text-text-body">
                <input type="checkbox" checked={i.needCompactor} onChange={(e) => set({ needCompactor: e.target.checked })} />
                {c.addCompactor}
                <InfoTip text={c.addCompactorTip} />
              </label>
            )}
          </div>

          {/* Waste */}
          <div className="mt-5 rounded-lg bg-surface-2 p-3">
            <div className="flex items-center justify-between text-sm">
              <span className="flex items-center font-medium text-text-body">
                {tr('calc.wasteAllowance')}
                <InfoTip text={c.wasteTip} />
              </span>
              <span className="tnum text-brand-text">{i.wastePct}%</span>
            </div>
            <input
              type="range" min={5} max={15} step={1} value={i.wastePct}
              onChange={(e) => set({ wastePct: parseInt(e.target.value) })}
              className="mt-1 w-full accent-brand"
            />
            <p className="mt-2 text-xs text-text-subtle">
              {metric
                ? <>{r.totalAreaM2} m² × {depthCm} cm = {rawM3} m³ &rarr; {r.tons} {tons}</>
                : <>{r.totalArea} {sqft} × {i.depthIn} {inUnit} = {r.cubicFeet} {cuFt} &rarr; {r.cubicYards} {locale === 'fr' ? 'verges cubes' : 'cu yd'} bulk &middot; {r.tons} {tons}</>}
            </p>
          </div>
          <CalcActions onSave={onSave} saved={saved} locale={locale} className="mt-6 border-t border-border pt-4" />
        </form>

        <div className="lg:col-span-2">
          <ResultSheet
            primary={{ name: c.primaryName, label: c.primaryLabel, qty: volumeQty, unit: volumeUnit, query: `${i.gravelType.replace('-', ' ')} bulk delivery` }}
            secondary={{ name: c.secondaryName, label: c.secondaryLabel, qty: r.bags50lb, unit: 'bag', query: `${i.gravelType.replace('-', ' ')} 50 lb bag` }}
            lines={r.lines}
            assumptions={assumptions}
            explain={buildExplain()}
          />
        </div>
      </div>

      <article className="mx-auto mt-8 max-w-content text-text-body">
        <h2 className="text-2xl">{c.aboutH2}</h2>
        <p className="mt-3">{c.aboutP1}</p>
        <h2 className="mt-8 text-2xl">{c.howH2}</h2>
        <p className="mt-3">{c.howP1}</p>
        <pre className="mt-3 overflow-x-auto rounded-lg bg-surface-2 p-3 text-sm text-text">{c.formula}</pre>
        <p className="mt-3">{c.howP2}</p>
        <h3 className="mt-6 text-xl">{c.mistakesH3}</h3>
        {locale === 'fr' ? (
          <ul className="mt-2 list-disc space-y-1 pl-5">
            <li>Utiliser du <strong>gravier rond sur une entrée</strong> — le gravier de pois se déplace et ne se compacte pas; utilisez la pierre concassée.</li>
            <li>Omettre la <strong>membrane géotextile</strong> — sans elle, les mauvaises herbes traversent le gravier dès la première saison.</li>
            <li>Sous-commander — ajoutez toujours au moins <strong>10 % en plus</strong> pour la profondeur tassée et le sol irrégulier.</li>
            <li>Confondre <strong>verges cubes et tonnes</strong> en appelant le fournisseur — confirmez l’unité utilisée pour le prix.</li>
          </ul>
        ) : (
          <ul className="mt-2 list-disc space-y-1 pl-5">
            <li>Using <strong>round gravel on driveways</strong> — pea gravel shifts and doesn't compact; use crushed stone.</li>
            <li>Skipping <strong>landscape fabric</strong> — weeds come through the gravel within one season without it.</li>
            <li>Under-ordering — always add at least <strong>10% extra</strong> for settled depth and irregular ground.</li>
            <li>Confusing <strong>cubic yards vs. tons</strong> when calling the supplier — confirm which unit they use for pricing.</li>
          </ul>
        )}
      </article>

      <Faq items={c.faqs} />

      <section className="mt-10">
        <h2 className="text-2xl">{c.relatedH2}</h2>
        <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3">
          {related.map((x) => (
            <a
              key={x.slug}
              href={L(`/calculators/${x.slug}/`)}
              className={`rounded-lg border p-3 text-sm transition-colors ${
                x.live
                  ? 'border-border bg-surface-2 font-medium text-brand-text hover:border-brand hover:bg-surface'
                  : 'border-dashed border-border bg-surface-2 text-text-muted'
              }`}
            >
              {calcName(x.slug, x.name, locale)} {x.live ? '' : <span className="text-text-subtle">{tr('calc.soon')}</span>}
            </a>
          ))}
        </div>
      </section>

      <div className="mx-auto mt-8 max-w-content border-t border-border pt-4 text-xs text-text-subtle">{c.reviewer}</div>
    </div>
  )
}
